import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info, Reply, Forward, Star, Trash2 } from 'lucide-react';

export const REACTION_EMOJIS = ['😠', '🙈', '😎', '👏', '👍', '😉', '❤️'];

/**
 * Per-message action popover (WhatsApp-style): quick emoji reaction bar on
 * top + action list (details / reply / forward / star / delete).
 *
 * Rendered via a portal into document.body with viewport-fixed coordinates
 * derived from `anchorRect` (the message bubble's on-screen position at open
 * time) — NOT position:absolute relative to the bubble. An absolutely
 * positioned child can't escape an ancestor's overflow:hidden/auto, so a
 * bubble inside any clipped/scrollable container (e.g. the Mini Chat Dock's
 * narrow floating window) would otherwise render this popover cut off.
 */
const MessageActionsMenu = ({
  message,
  isOpen,
  onClose,
  onShowDetails,
  onReply,
  onForward,
  onToggleStar,
  isStarred,
  onReact,
  myReaction,
  onDelete,
  isDeletable,
  c,
  t,
  isRTL,
  anchorRect, // DOMRect of the bubble area, captured by the caller on open
  // When the bubble sits low in the viewport the caller asks the menu to
  // open upward so it doesn't clip below the scroll container.
  opensUp = false,
}) => {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null); // { top, left } in viewport px

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Compute + clamp against the ACTUAL rendered size so the menu never
  // overflows the viewport, however narrow its container is. First paint
  // happens off-screen (see style below) purely to measure offsetWidth/Height.
  useLayoutEffect(() => {
    if (!isOpen || !anchorRect) { setPos(null); return; }
    const GAP = 4;
    const MARGIN = 8;
    const menuWidth = menuRef.current?.offsetWidth || 220;
    const menuHeight = menuRef.current?.offsetHeight || 260;

    const anchorToRightEdge = message.sent ? !isRTL : isRTL;

    let left = anchorToRightEdge ? anchorRect.right - menuWidth : anchorRect.left;
    left = Math.min(Math.max(left, MARGIN), window.innerWidth - menuWidth - MARGIN);

    let top = opensUp ? anchorRect.top - GAP - menuHeight : anchorRect.bottom + GAP;
    top = Math.min(Math.max(top, MARGIN), window.innerHeight - menuHeight - MARGIN);

    setPos({ top, left });
  }, [isOpen, anchorRect, isRTL, message.sent, opensUp]);

  if (!isOpen) return null;

  // Entries without a handler (e.g. reply/forward/delete hidden by
  // admin-level feature gating) are dropped instead of rendering dead rows.
  const actions = [
    { icon: <Info size={15} />, label: t.messageDetails, onClick: onShowDetails },
    { icon: <Reply size={15} />, label: t.reply, onClick: onReply },
    { icon: <Forward size={15} />, label: t.forwardAction, onClick: onForward },
    {
      icon: <Star size={15} color={isStarred ? '#f59e0b' : undefined} fill={isStarred ? '#f59e0b' : 'none'} />,
      label: isStarred ? t.unstarAction : t.starAction,
      onClick: onToggleStar,
    },
    ...(isDeletable && onDelete
      ? [{ icon: <Trash2 size={15} />, label: t.deleteAction, onClick: onDelete, danger: true }]
      : []),
  ].filter((action) => typeof action.onClick === 'function');

  return createPortal(
    <div
      ref={menuRef}
      onClick={(e) => e.stopPropagation()}
      data-modal-root="true"
      style={{
        position: 'fixed',
        top: pos ? pos.top : -9999,
        left: pos ? pos.left : -9999,
        visibility: pos ? 'visible' : 'hidden',
        zIndex: 100000,
        background: c.popoverBg || c.sidebarBg,
        border: `1px solid ${c.popoverBorder || c.border}`,
        borderRadius: '16px',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.22)',
        padding: '8px',
        minWidth: '210px',
        animation: 'popoverSlideUp 0.15s ease',
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      {/* Quick reactions */}
      {typeof onReact === 'function' && (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '2px',
          padding: '4px 4px 8px',
          borderBottom: `1px solid ${c.border}`,
          marginBottom: '6px',
        }}
      >
        {REACTION_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => { onReact(emoji); onClose(); }}
            title={emoji}
            style={{
              border: 'none',
              background: myReaction === emoji ? (c.hoverBg || 'rgba(46,124,186,0.15)') : 'transparent',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '16px',
              lineHeight: 1,
            }}
          >
            {emoji}
          </button>
        ))}
      </div>
      )}

      {/* Actions */}
      {actions.map((action) => (
        <button
          key={action.label}
          type="button"
          onClick={() => { action.onClick(); onClose(); }}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '9px 10px',
            border: 'none',
            background: 'transparent',
            color: action.danger ? '#ef4444' : c.text,
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '13.5px',
            fontWeight: '600',
            textAlign: 'start',
          }}
        >
          {action.icon}
          {action.label}
        </button>
      ))}
    </div>,
    document.body
  );
};

export default MessageActionsMenu;
